<script setup lang="ts">
/**
 * A small read-only grid of values — a dialog's preview of a few cells (the
 * consolidation result, an import sample). No sorting, no selection, no
 * settings, no kit i18n needed: a plain `<table>` in NxTable's look.
 *
 * `rows` is a matrix of values; each is shown as text (`null` / `undefined`
 * as an empty cell), numbers right-aligned with digits of one width. Shorter
 * rows are padded with empty cells. `columnHeaders` adds a head row,
 * `rowHeaders` a leading header column. For anything with interaction or many
 * rows use `NxTable`.
 */
import { computed, type VNode } from 'vue';
import { NxTabularNumber } from '../TabularNumber';

const props = withDefaults(
  defineProps<{
    /** The values, row by row. */
    rows: readonly (readonly unknown[])[];
    /** Captions of the columns, shown as a head row. */
    columnHeaders?: readonly string[];
    /** Captions of the rows, shown as a leading header column. */
    rowHeaders?: readonly string[];
    /** A visible caption above the table (also its accessible name). */
    caption?: string;
    /** Accessible name without a visible caption. */
    ariaLabel?: string;
    /** Tighter cells. */
    dense?: boolean;
    /**
     * Turns a value into its text (a locale's number format). Default:
     * `String(value)`, empty for `null` / `undefined`.
     */
    formatValue?: (value: unknown) => string;
  }>(),
  {
    columnHeaders: undefined,
    rowHeaders: undefined,
    caption: undefined,
    ariaLabel: undefined,
    dense: false,
    formatValue: undefined,
  }
);

defineSlots<{
  /** Optional content for a column header, such as an import column's format. */
  'column-header'?: (props: { label: string; columnIndex: number }) => VNode[];
}>();

interface Cell {
  text: string;
  numeric: boolean;
}

const columnCount = computed(() =>
  Math.max(props.columnHeaders?.length ?? 0, ...props.rows.map((row) => row.length), 0)
);

function text(value: unknown): string {
  if (props.formatValue) return props.formatValue(value);
  return value === null || value === undefined ? '' : String(value);
}

const body = computed<Cell[][]>(() =>
  props.rows.map((row) =>
    Array.from({ length: columnCount.value }, (_, index) => {
      const value = row[index];
      return {
        text: text(value),
        numeric: typeof value === 'number' || typeof value === 'bigint',
      };
    })
  )
);

const hasRowHeaders = computed(() => !!props.rowHeaders?.length);
</script>

<template>
  <table
    class="nx-simple-table"
    :class="{ 'is-dense': props.dense }"
    :aria-label="props.caption ? undefined : props.ariaLabel"
    data-testid="nx-simple-table"
  >
    <caption v-if="props.caption" class="nx-simple-table__caption">
      {{ props.caption }}
    </caption>
    <thead v-if="props.columnHeaders?.length">
      <tr>
        <td v-if="hasRowHeaders" class="nx-simple-table__corner"></td>
        <th v-for="column in columnCount" :key="column" scope="col" class="nx-simple-table__header">
          <slot
            name="column-header"
            :label="props.columnHeaders[column - 1] ?? ''"
            :column-index="column - 1"
          >
            {{ props.columnHeaders[column - 1] ?? '' }}
          </slot>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(row, rowIndex) in body" :key="rowIndex">
        <th v-if="hasRowHeaders" scope="row" class="nx-simple-table__header is-row">
          {{ props.rowHeaders?.[rowIndex] ?? '' }}
        </th>
        <td
          v-for="(cell, columnIndex) in row"
          :key="columnIndex"
          class="nx-simple-table__cell"
          :class="{ 'is-numeric': cell.numeric }"
        >
          <NxTabularNumber v-if="cell.numeric" :value="cell.text" />
          <template v-else>{{ cell.text }}</template>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>
.nx-simple-table {
  --simple-table-pad-y: var(--nxt-space-2xs);
  --simple-table-pad-x: var(--nxt-space-sm);

  border-collapse: collapse;
  max-width: 100%;
  color: var(--nxt-table-cell-fg);
  background: var(--nxt-table-row-bg);
  font-size: var(--nxt-table-size);
  line-height: var(--nxt-line-height-normal);
}
.nx-simple-table.is-dense {
  --simple-table-pad-y: 0;
  --simple-table-pad-x: var(--nxt-space-xs);
}
.nx-simple-table__caption {
  padding-block-end: var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  text-align: start;
}
.nx-simple-table__cell,
.nx-simple-table__header,
.nx-simple-table__corner {
  padding: var(--simple-table-pad-y) var(--simple-table-pad-x);
  border: var(--nxt-hairline) solid var(--nxt-table-border);
  text-align: start;
  vertical-align: middle;
  white-space: nowrap;
}
.nx-simple-table__header,
.nx-simple-table__corner {
  background: var(--nxt-table-header-bg);
  color: var(--nxt-table-header-fg);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-simple-table__cell.is-numeric {
  text-align: end;
}
</style>
